import React, { useCallback, useEffect, useRef, useState } from 'react';
import soundstatsService from '../../services/soundstatsService';
import TabBar from './TabBar';
import Overview from './Overview';
import GroupBody from './GroupBody';
import {
  CATEGORIES, buildNormalized, titleCase, initial, fmtNum,
  readCache, writeCache, pruneCache,
} from './soundstatsHelpers';
import './soundstats.css';

// Inline Soundcharts artist-intelligence dashboard. Loads the 'core' overview
// on mount, then lazily fetches the other 12 groups on tab click with a 24-hour
// localStorage cache. Credentials stay server-side; the artist UUID comes from
// the clicked search card. Availability-based hiding: unavailable sections are
// omitted and a quota banner shows when core returns any errors.

const CORE = 'core';

export default function SoundstatsArtistDashboard({ uuid, onBack, backLabel = '← Back to search', overviewOnly = false }) {
  const [core, setCore] = useState({ status: 'loading', N: null, AVAIL: {}, hasErrors: false, error: '' });
  const [activeGroup, setActiveGroup] = useState(CORE);
  const [groupData, setGroupData] = useState({});
  const [groupStatus, setGroupStatus] = useState({});
  const [chipIdx, setChipIdx] = useState(0);
  const groupCacheRef = useRef({});

  const fetchGroup = useCallback(async group => {
    const cached = groupCacheRef.current[group] || readCache(uuid, group);
    if (cached) { groupCacheRef.current[group] = cached; return cached; }
    const d = await soundstatsService.getArtistIntelligence(uuid, group);
    if (d && d.error) throw new Error(d.error);
    groupCacheRef.current[group] = d;
    writeCache(uuid, group, d);
    return d;
  }, [uuid]);

  const loadCore = useCallback(async () => {
    setCore({ status: 'loading', N: null, AVAIL: {}, hasErrors: false, error: '' });
    try {
      const json = await fetchGroup(CORE);
      const { N, AVAIL, hasErrors } = buildNormalized(json);
      setCore({ status: 'ready', N, AVAIL, hasErrors, error: '' });
    } catch (e) {
      setCore({ status: 'error', N: null, AVAIL: {}, hasErrors: false, error: (e && e.message) || 'Could not load artist data.' });
    }
  }, [fetchGroup]);

  useEffect(() => {
    pruneCache();
    groupCacheRef.current = {};
    loadCore();
  }, [loadCore]);

  const selectGroup = useCallback(group => {
    setActiveGroup(group);
    setChipIdx(0);
    if (group === CORE) return;
    if (groupData[group]) return;
    setGroupStatus(s => ({ ...s, [group]: 'loading' }));
    fetchGroup(group)
      .then(json => {
        setGroupData(d => ({ ...d, [group]: json }));
        setGroupStatus(s => ({ ...s, [group]: 'ready' }));
      })
      .catch(e => {
        setGroupStatus(s => ({ ...s, [group]: 'error' }));
        setGroupData(d => ({ ...d, [group]: { _error: (e && e.message) || 'Failed to load.' } }));
      });
  }, [fetchGroup, groupData]);

  const { status, N, AVAIL, error } = core;

  if (status === 'loading') {
    return (
      <div className="ss-wrap">
        <button type="button" className="ss-back" onClick={onBack}>{backLabel}</button>
        <div className="ss-state"><div className="ss-spinner" /><p>Loading artist intelligence…</p></div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="ss-wrap">
        <button type="button" className="ss-back" onClick={onBack}>{backLabel}</button>
        <div className="ss-state">
          <div className="ss-err-badge">Request failed</div>
          <p>{error}</p>
          <button type="button" className="ss-retry" onClick={loadCore}>Try again</button>
        </div>
      </div>
    );
  }

  const meta = N?.meta || {};
  const latest = N?.score?.length ? N.score[N.score.length - 1] : {};
  const anySection = AVAIL.meta || AVAIL.score ||
    AVAIL.statsSocial || AVAIL.statsStreaming || AVAIL.statsPopularity || AVAIL.statsRetention ||
    AVAIL.albums || AVAIL.songs || AVAIL.identifiers ||
    AVAIL.broadcastGroups || AVAIL.broadcasts || AVAIL.events || AVAIL.related;

  if (!anySection) {
    return (
      <div className="ss-wrap">
        <button type="button" className="ss-back" onClick={onBack}>{backLabel}</button>
        <div className="ss-state"><p>No intelligence data available for this artist.</p></div>
      </div>
    );
  }

  const activeJson = activeGroup !== CORE ? groupData[activeGroup] : null;
  const activeStatus = activeGroup !== CORE ? groupStatus[activeGroup] : null;
  const groupErr = activeJson?._error;

  return (
    <div className="ss-wrap">
      <button type="button" className="ss-back" onClick={onBack}>{backLabel}</button>

      {AVAIL.meta && (
        <section className="ss-hero">
          <div className="ss-hero-left">
            <div className={`ss-logo${meta.imageUrl ? '' : ' ph'}`} data-initial={initial(meta.name || '')}>
              {meta.imageUrl && <img src={meta.imageUrl} alt={meta.name || ''} className="ss-avatar" />}
            </div>
            <div className="ss-hero-title">
              <div className="ss-badges">
                {meta.careerStage && <span className="ss-badge accent">{titleCase(meta.careerStage)}</span>}
                {meta.growthLevel && <span className="ss-badge">{titleCase(meta.growthLevel)}</span>}
                {meta.type && <span className="ss-badge">{titleCase(meta.type)}</span>}
              </div>
              <h1 className="ss-name">{meta.name || `Artist ${uuid || ''}`}</h1>
              <div className="ss-sub">
                {meta.gender && <span>{titleCase(meta.gender)}</span>}
                {meta.countryCode && <span>{meta.countryCode}</span>}
                {meta.webUrl && <a href={meta.webUrl} target="_blank" rel="noopener noreferrer">Website</a>}
                {uuid && <span className="ss-uuid">{uuid}</span>}
              </div>
              <div className="ss-chips">{(meta.genres || []).map((g, i) => <span key={i} className="ss-chip">{g.root}</span>)}</div>
            </div>
          </div>
          {AVAIL.score && (
            <div className="ss-hero-right">
              <div className="ss-stat-strip">
                <div className="ss-stat"><strong>{latest.scScore != null ? fmtNum(latest.scScore) : '—'}</strong><span>SC Score</span></div>
                <div className="ss-stat"><strong>{latest.fanbaseScore != null ? fmtNum(latest.fanbaseScore) : '—'}</strong><span>Fanbase</span></div>
                <div className="ss-stat"><strong>{latest.trendingScore != null ? fmtNum(latest.trendingScore) : '—'}</strong><span>Trending</span></div>
              </div>
            </div>
          )}
        </section>
      )}

      {!overviewOnly && <TabBar categories={CATEGORIES} active={activeGroup} onSelect={selectGroup} />}

      {overviewOnly || activeGroup === CORE ? (
        <Overview N={N} AVAIL={AVAIL} collapsible={overviewOnly} />
      ) : activeStatus === 'loading' && !activeJson ? (
        <div className="ss-state"><div className="ss-spinner" /><p>Loading {CATEGORIES.find(c => c.id === activeGroup)?.label || activeGroup}…</p></div>
      ) : groupErr ? (
        <div className="ss-state"><div className="ss-err-badge">Request failed</div><p>{groupErr}</p></div>
      ) : activeJson ? (
        <GroupBody group={activeGroup} json={activeJson} chipIdx={chipIdx} setChipIdx={setChipIdx} />
      ) : null}
    </div>
  );
}
